import React, { useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { uploadAvatar } from '../../services/api';

const AvatarInput = styled('input')({
  display: 'none',
});

const AvatarPreview = styled('img')({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid rgba(255, 255, 255, 0.12)',
  flexShrink: 0,
});

interface AvatarSettingsFormProps {
  currentAvatar: string | null;
  onAvatarUpdate: (newAvatarUrl: string) => void;
}

const AvatarSettingsForm: React.FC<AvatarSettingsFormProps> = ({ currentAvatar, onAvatarUpdate }) => {
  const { t } = useTranslation('settings');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(currentAvatar);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError(t('avatar.errorTooLarge'));
        return;
      }

      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setError(t('avatar.errorInvalidType'));
        return;
      }

      setAvatarFile(file);
      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!avatarFile) return;

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('avatar', avatarFile);
      
      const response = await uploadAvatar(formData);
      if (response.avatar_url) {
        onAvatarUpdate(response.avatar_url);
        setAvatarFile(null);
      }
    } catch (error: any) {
      console.error('Avatar upload error:', error);
      setError(
        error.response?.data?.avatar?.[0] ||
        error.response?.data?.error ||
        t('avatar.errorUpdateFailed')
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: '100%', maxWidth: 600, mb: 3 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <AvatarPreview
          src={avatarPreview || '/default-avatar.png'}
          alt={t('avatar.previewAlt')}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <label htmlFor="avatar-input">
              <AvatarInput
                accept="image/jpeg,image/png,image/gif"
                id="avatar-input"
                type="file"
                onChange={handleAvatarChange}
              />
              <Button
                variant="outlined"
                size="small"
                component="span"
                disabled={isUploading}
              >
                {t('avatar.selectImage')}
              </Button>
            </label>
            {avatarFile && (
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={isUploading}
              >
                {isUploading ? t('avatar.uploading') : t('save', { ns: 'common' })}
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
            {t('avatar.formatHint')}
          </Typography>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AvatarSettingsForm;
